/**
 * @file    useEngineChartTypeResolver.ts
 * @scope   ENTERPRISE
 * @summary Drawer-side hooks returning the flag-aware chart-type resolvers to inject into `common/`.
 * @consumes engine feature-flag resolver (configDashboard + localStorage), engine registry
 * @produces useEngineChartTypeResolver, useEngineConfigChartTypeResolver
 * @sideEffects None.
 * @notes   Isolation rule: `common/` keeps receiving a plain resolver function; only this seam knows
 *          about flags. Stable reference while `configDashboard` doesn't change.
 */
import { useMemo } from 'react';
import {
  createConfigChartTypeResolver,
  createEnterpriseChartTypeResolver,
  createGatedOptionPredicate,
} from 'components/DiyWidgetEngine/ChartTypes/engineChartTypeRegistry';
import { useFeatureFlagResolver } from 'components/DiyWidgetEngine/engine/featureFlags/useFeatureFlagResolver';

export const useEngineChartTypeResolver = () => {
  const isFlagEnabled = useFeatureFlagResolver();
  return useMemo(() => createEnterpriseChartTypeResolver(isFlagEnabled), [isFlagEnabled]);
};

/** Flag-aware resolver for SAVED configs (envelope / `Chart`), injected into `useCurrentAlias`. */
export const useEngineConfigChartTypeResolver = () => {
  const isFlagEnabled = useFeatureFlagResolver();
  return useMemo(() => createConfigChartTypeResolver(isFlagEnabled), [isFlagEnabled]);
};

/** `(optionValue) => boolean`: hides dropdown options of gated-off engine subtypes. */
export const useEngineGatedOptionPredicate = () => {
  const isFlagEnabled = useFeatureFlagResolver();
  return useMemo(() => createGatedOptionPredicate(isFlagEnabled), [isFlagEnabled]);
};

export default useEngineChartTypeResolver;
